import { Link, type LinkProps } from "@tanstack/react-router";
import { cn } from "@/lib/cn";

export interface TabItem {
  label: string;
  value: string;
  count?: number;
}

export interface TabsProps {
  items: TabItem[];
  value: string;
  onChange: (value: string) => void;
  className?: string;
}

const TAB_BASE =
  "relative flex items-center gap-1.5 whitespace-nowrap pb-3 text-small font-medium transition-colors focus-visible:outline-2 focus-visible:outline-primary";

/** State-based tabs (e.g. profile Posts/Products/Likes/Collections). */
export function Tabs({ items, value, onChange, className }: TabsProps) {
  return (
    <div role="tablist" className={cn("flex gap-6 border-b border-border", className)}>
      {items.map((item) => {
        const active = item.value === value;
        return (
          <button
            key={item.value}
            type="button"
            role="tab"
            aria-selected={active}
            onClick={() => onChange(item.value)}
            className={cn(TAB_BASE, active ? "text-text-primary" : "text-text-secondary hover:text-text-primary")}
          >
            {item.label}
            {item.count !== undefined && <span className="tnum text-text-muted">{item.count}</span>}
            {active && <span className="absolute inset-x-0 -bottom-px h-0.5 bg-primary" aria-hidden="true" />}
          </button>
        );
      })}
    </div>
  );
}

export interface LinkTabItem {
  label: string;
  to: LinkProps["to"];
  search?: LinkProps["search"];
  count?: number;
}

/** URL-based tabs — active state driven by the router's active-link match. */
export function LinkTabs({ items, className }: { items: LinkTabItem[]; className?: string }) {
  return (
    <nav className={cn("flex gap-6 border-b border-border", className)}>
      {items.map((item) => (
        <Link
          key={item.label}
          to={item.to}
          search={item.search}
          className={cn(TAB_BASE, "text-text-secondary")}
          activeProps={{ className: "text-text-primary" }}
          activeOptions={{ exact: true }}
        >
          {({ isActive }: { isActive: boolean }) => (
            <>
              {item.label}
              {item.count !== undefined && <span className="tnum text-text-muted">{item.count}</span>}
              {isActive && <span className="absolute inset-x-0 -bottom-px h-0.5 bg-primary" aria-hidden="true" />}
            </>
          )}
        </Link>
      ))}
    </nav>
  );
}
